import { atom } from 'jotai'
import type { ChartPreferences } from '@shared/schemas/chartPreferences'

export interface Organization {
  id: string
  name: string
  slug: string
  description?: string
  website?: string
  logoUrl?: string // Legacy field, kept for backward compatibility
  logos?: {
    light?: {
      url: string
      path: string
    }
    dark?: {
      url: string
      path: string
    }
  }
  createdAt: Date
  updatedAt: Date
  ownerId: string
  memberIds: string[] // Array of user IDs who are members
  memberCount: number
  plan: 'free' | 'pro' | 'enterprise'
  settings: {
    publicJoin: boolean
    requireApproval: boolean
    maxMembers: number
  }
  chartPreferences?: ChartPreferences // Organization's default chart preferences
}

export interface OrganizationMember {
  id: string
  organizationId: string
  userId: string
  email: string
  displayName?: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  status: 'active' | 'pending' | 'suspended'
  joinedAt: Date
  invitedBy?: string
}

// Current user's organization
export const currentOrganizationAtom = atom<Organization | null>(null)

// Current user's membership in the organization
export const currentMembershipAtom = atom<OrganizationMember | null>(null)

// Organization setup flow state
export const organizationSetupAtom = atom<{
  step: 'none' | 'create' | 'join' | 'complete'
  loading: boolean
  error: string | null
}>({
  step: 'none',
  loading: false,
  error: null
})

// Derived atom to check if user has an organization
export const hasOrganizationAtom = atom((get) => {
  const org = get(currentOrganizationAtom)
  return org !== null
})

// Derived atom to check if user can save charts (has organization and proper role)
export const canSaveChartsAtom = atom((get) => {
  const org = get(currentOrganizationAtom)
  const membership = get(currentMembershipAtom)
  
  return org !== null && 
         membership !== null && 
         membership.status === 'active' &&
         ['owner', 'admin', 'member'].includes(membership.role)
})

// Action atom for setting organization setup step
export const setOrganizationSetupStepAtom = atom(
  null,
  (get, set, step: 'none' | 'create' | 'join' | 'complete') => {
    const current = get(organizationSetupAtom)
    set(organizationSetupAtom, { ...current, step })
  }
)

// Action atom for setting organization setup loading
export const setOrganizationSetupLoadingAtom = atom(
  null,
  (get, set, loading: boolean) => {
    const current = get(organizationSetupAtom)
    set(organizationSetupAtom, { ...current, loading })
  }
)

// Action atom for setting organization setup error
export const setOrganizationSetupErrorAtom = atom(
  null,
  (get, set, error: string | null) => {
    const current = get(organizationSetupAtom)
    set(organizationSetupAtom, { ...current, error })
  }
) 